import type { MenuItem } from "@/stores/menu";

const normalizePath = (path = "") => {
  const trimmedPath = path.trim();
  if (!trimmedPath) return "";

  let normalizedPath = trimmedPath.replace(/\/+/g, "/");
  if (!normalizedPath.startsWith("/")) {
    normalizedPath = `/${normalizedPath}`;
  }

  if (normalizedPath.length > 1) {
    normalizedPath = normalizedPath.replace(/\/+$/, "");
  }

  return normalizedPath;
};

const resolveMenuPath = (menu: MenuItem, parentPath = "") => {
  const currentPath = menu.path || "";

  if (!currentPath) {
    return normalizePath(parentPath);
  }

  if (currentPath.startsWith("/")) {
    return normalizePath(currentPath);
  }

  const normalizedParent = normalizePath(parentPath);
  return normalizePath(
    normalizedParent ? `${normalizedParent}/${currentPath}` : currentPath,
  );
};

export const hasMenuPathAccess = (
  menus: MenuItem[],
  targetPath: string,
  parentPath = "",
): boolean => {
  const normalizedTargetPath = normalizePath(targetPath);

  return menus.some((menu) => {
    const menuPath = resolveMenuPath(menu, parentPath);

    if (menuPath === normalizedTargetPath) {
      return true;
    }

    // 允许访问已授权菜单下的子路由
    // 例如：菜单里只有 /promotion/facebook，但前端路由会跳转到 /promotion/facebook/campaigns
    if (
      menuPath &&
      normalizedTargetPath.length > menuPath.length &&
      normalizedTargetPath.startsWith(`${menuPath}/`)
    ) {
      return true;
    }

    if (menu.children?.length) {
      return hasMenuPathAccess(menu.children, normalizedTargetPath, menuPath);
    }

    return false;
  });
};

//获取第一个菜单路径
export const getFirstMenuPath = (menus: MenuItem[], parentPath = ""): string => {
  if (!menus?.length) return "";

  const sorted = [...menus].sort((a, b) => (a.sort || 0) - (b.sort || 0));
  const first = sorted[0];
  if (!first) return "";

  const currentPath = resolveMenuPath(first, parentPath);

  if (first.children?.length) {
    const childPath = getFirstMenuPath(first.children, currentPath);
    if (childPath) return childPath;
  }

  return currentPath;
};

export const canAccessDramaData = (menus: MenuItem[]) => {
  return hasMenuPathAccess(menus, "/data/drama");
};

/** 刷新菜单后校验当前路由是否仍允许访问 */
export const isPathAllowedAfterMenuSync = (
  menus: MenuItem[],
  targetPath: string,
): boolean => {
  const path = normalizePath(targetPath);
  if (!path || path === "/home") {
    return true;
  }
  if (path.startsWith("/ad/create")) {
    return hasMenuPathAccess(menus, "/promotion");
  }
  return hasMenuPathAccess(menus, path);
};
